import React from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { LoadingComponent } from 'recupero-comunes'
import KpiCard from '../dashboard-comunes/kpi-card'
import AvisoExportacion from '../dashboard-comunes/aviso-exportacion'
import CerradosPorProveedor from '../dashboard-comunes/cerrados-por-proveedor'
import CasosActivosPorProveedor from '../dashboard-comunes/casos-activos-por-proveedor'
import { formatMonto, formatMesAnio } from '../../common/dashboard-utils'
import { useExportarCasos } from '../../common/use-exportar-casos'
import * as estilos from '../../common/dashboard-estilos'

/** Etiqueta legible del período activo, para el encabezado de los KPI filtrados. */
function describePeriodo(filtros, mesesDisponibles) {
    if (filtros.mes) {
        const etiqueta = mesesDisponibles.find((m) => m.valor === filtros.mes)?.etiqueta
        return etiqueta || filtros.mes
    }
    if (filtros.criteriosFecha && filtros.fechadesde && filtros.fechahasta) {
        const formatFecha = (d) => d.split('-').reverse().join('/')
        return `${filtros.criteriosFecha}: ${formatFecha(filtros.fechadesde)} → ${formatFecha(filtros.fechahasta)}`
    }
    return 'Todos los períodos'
}

const ResumenGeneral = () => {
    const resumen = useSelector((state) => state.dashboardDirectoReducer.resumen)
    const kpisGlobales = useSelector((state) => state.dashboardDirectoReducer.kpisGlobales)
    const filtros = useSelector((state) => state.dashboardDirectoReducer.filtros)
    const mesesDisponibles = useSelector((state) => state.dashboardDirectoReducer.mesesDisponibles)

    // RG-602: cada tarjeta y cada barra descarga los casos que hay detrás de su número.
    const { exportar, exportando, aviso, cierraAviso } = useExportarCasos('directo')
    const exportaConcepto = (concepto, extras) => () => exportar(concepto, extras)

    const kpis = resumen?.kpis || {}
    const casosPorProveedor = kpisGlobales?.casosPorProveedor || []
    const cerradosPorProveedor = kpisGlobales?.cerradosPorProveedor || []

    const totalCargados = kpisGlobales?.totalCargados ?? '—'
    const vigentesSinRecupero = kpisGlobales?.gye?.vigentesSinRecupero ?? '—'
    const recuperadosMesAnterior = kpisGlobales?.gye?.recuperadosMesAnterior ?? '—'
    const montoRecobradoMesAnterior = kpisGlobales?.gye?.montoRecobradoMesAnterior ?? null
    const recuperadosMesEnCurso = kpisGlobales?.gye?.recuperadosMesEnCurso ?? '—'
    const montoRecobradoMesEnCurso = kpisGlobales?.gye?.montoRecobradoMesEnCurso ?? null
    const recuperadosUltimoAnio = kpisGlobales?.gye?.recuperadosUltimoAnio ?? '—'
    const montoRecobradoUltimoAnio = kpisGlobales?.gye?.montoRecobradoUltimoAnio ?? null
    const mesAnteriorLabel = formatMesAnio(kpisGlobales?.gye?.mesAnteriorLabel)
    const mesEnCursoLabel = formatMesAnio(kpisGlobales?.gye?.mesEnCursoLabel)
    const derivadosOtros = kpisGlobales?.derivadosOtrosGestores ?? '—'

    const periodoLabel = describePeriodo(filtros, mesesDisponibles)

    return (
        <div>
            <LoadingComponent show={exportando} />
            <AvisoExportacion aviso={aviso} onClose={cierraAviso} />

            {/* Indicadores históricos: no dependen del filtro de período. */}
            <Card className='mb-3' style={{ borderLeft: '4px solid #343a40', background: '#f8f9fa' }}>
                <Card.Header as='h6' style={{ ...estilos.headerCard, backgroundColor: '#343a40' }}>
                    Resumen Global — Todos los Períodos
                </Card.Header>
                <Card.Body className='py-2'>
                    <Row className='g-2 mb-2'>
                        <Col xs={6} md={4}>
                            <KpiCard
                                label='Total Casos Asignados'
                                value={totalCargados}
                                color='#343a40'
                                onExportar={exportaConcepto('totalAsignados')}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={4}>
                            <KpiCard
                                label='En Gestión GYE'
                                value={vigentesSinRecupero}
                                subLabel='Recepcionado + Caso Reasignado'
                                color='#dc3545'
                                onExportar={exportaConcepto('enGestionGye')}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={4}>
                            <KpiCard
                                label='Derivados Otro Gestor'
                                value={derivadosOtros}
                                subLabel='Responsable activo ≠ GYE'
                                color='#fd7e14'
                                onExportar={exportaConcepto('derivadosOtros')}
                                exportando={exportando}
                            />
                        </Col>
                    </Row>
                    <Row className='g-2'>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label={`Recuperados GYE — ${mesAnteriorLabel}`}
                                value={recuperadosMesAnterior}
                                subLabel='Mes anterior completo'
                                color='#28a745'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'mesAnterior' })}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label={`Monto Recobrado GYE — ${mesAnteriorLabel}`}
                                value={formatMonto(montoRecobradoMesAnterior)}
                                subLabel='Mes anterior completo'
                                color='#28a745'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'mesAnterior' })}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label={`Recuperados GYE — ${mesEnCursoLabel}`}
                                value={recuperadosMesEnCurso}
                                subLabel='Mes en curso (hasta hoy)'
                                color='#17a2b8'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'mesEnCurso' })}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label={`Monto Recobrado GYE — ${mesEnCursoLabel}`}
                                value={formatMonto(montoRecobradoMesEnCurso)}
                                subLabel='Mes en curso (hasta hoy)'
                                color='#17a2b8'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'mesEnCurso' })}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label='Recuperados GYE — Últ. Año'
                                value={recuperadosUltimoAnio}
                                subLabel={`Año ${new Date().getFullYear()}`}
                                color='#20c997'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'anio' })}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <KpiCard
                                label='Monto Recobrado GYE — Últ. Año'
                                value={formatMonto(montoRecobradoUltimoAnio)}
                                subLabel={`Año ${new Date().getFullYear()}`}
                                color='#20c997'
                                onExportar={exportaConcepto('recuperadosGye', { periodo: 'anio' })}
                                exportando={exportando}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Indicadores del período seleccionado en el panel de filtros. */}
            <Card className='mb-3' style={{ borderLeft: '4px solid #1a6fc4' }}>
                <Card.Header as='h6' style={{ ...estilos.headerCard, backgroundColor: '#1a6fc4' }}>
                    Período seleccionado:&nbsp;
                    <span className='badge' style={{ background: '#fff', color: '#1a6fc4', fontSize: '0.78rem' }}>{periodoLabel}</span>
                </Card.Header>
                <Card.Body className='py-2'>
                    <Row className='g-2'>
                        <Col xs={6} md={3}>
                            <KpiCard
                                label='Casos Asignados'
                                value={kpis.totalCasos ?? '—'}
                                color='#4F517A'
                                onExportar={exportaConcepto('casosPeriodo')}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <KpiCard
                                label='Cerrado con Recupero'
                                value={kpis.casosConRecupero ?? '—'}
                                subLabel='Cerrados en el período'
                                color='#28a745'
                                onExportar={exportaConcepto('cerradoConRecupero')}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <KpiCard
                                label='Cerrado sin Recupero'
                                value={kpis.casosSinRecupero ?? '—'}
                                subLabel='Cerrados en el período'
                                color='#c0392b'
                                onExportar={exportaConcepto('cerradoSinRecupero')}
                                exportando={exportando}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <KpiCard
                                label='Monto Recobrado'
                                value={formatMonto(kpis.montoRecobrado)}
                                subLabel={kpis.ratioRecupero != null ? `${kpis.ratioRecupero}% del total` : undefined}
                                color='#28a745'
                                onExportar={exportaConcepto('cerradoConRecupero')}
                                exportando={exportando}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Row className='g-3 mb-3'>
                <Col xs={12} md={5}>
                    <Card className='h-100'>
                        <Card.Header as='h6' className='text-center' style={estilos.headerCard}>
                            Cerrados por Proveedor — Con y Sin Recupero — Todos los Períodos
                        </Card.Header>
                        <Card.Body style={estilos.chartContainer}>
                            <CerradosPorProveedor
                                data={cerradosPorProveedor}
                                onExportar={(responsableId, resultado) =>
                                    exportar('cerradosProveedor', { responsableId, resultado })
                                }
                            />
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={7}>
                    <Card className='h-100'>
                        <Card.Header as='h6' className='text-center' style={estilos.headerCard}>
                            Casos Activos por Proveedor — Todos los Períodos
                        </Card.Header>
                        <Card.Body style={estilos.chartContainer}>
                            <CasosActivosPorProveedor
                                data={casosPorProveedor}
                                onExportar={(responsableId) => exportar('casosActivosProveedor', { responsableId })}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default ResumenGeneral
